import { MemberStatusIcon } from "@components/table/members/members-column/status-column/member-status-icon";
import { TeamIcon, UserIcon } from "@flat-ui";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiConst, FaroCoreApiUtils } from "@stellar/core-api-client";
import { UtilsTypes } from "@stellar/utils";
import { formatUserRoleType } from "@utils/data-display";
import { getHeldSeats, getSeatsRank, hasAnyXgSeat, MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { getAssignableWorkspaceRoles } from "@utils/workspace-role-utils";

/** Same size as the team icon in the member lists */
const TYPE_ICON_SX = {
  width: "18px",
  height: "18px"
};

/** Returns the filter options for the status of the members, which teams never match */
export function getMembersStatusFilterOptions() {
  return Object.fromEntries(Object.entries(UtilsTypes.MemberStatus).map(([id, status]) => [id, {
    id,
    label: status,
    icon: <MemberStatusIcon status={status} />,
    hasEntityPassedFilter: member => !FaroCoreApiUtils.Member.isTeam(member) && FaroCoreApiUtils.Member.getMemberStatus(member) === status
  }]));
}

/** Returns the roles that can be held in the table, leaving out the ones the plan cannot assign */
export function getMembersTableRoles(tableSubject, canAssignManagers) {
  switch (tableSubject) {
    case "project":
      return [...FaroCoreApiConst.allProjectRoles];
    case "group":
      return [...FaroCoreApiConst.allGroupRoles];
    default:
      return getAssignableWorkspaceRoles("invite", canAssignManagers);
  }
}

/** Returns a filter option for each of the roles, in the given order */
export function getMembersRoleFilterOptions(roles) {
  return Object.fromEntries(roles.map(role => [role, {
    id: role,
    label: formatUserRoleType(role),
    hasEntityPassedFilter: member => member.role === role
  }]));
}

/** Returns the filter options to tell members and teams apart */
export function getMembersTypeFilterOptions() {
  return {
    member: {
      id: "member",
      label: "Member",
      icon: <UserIcon sx={TYPE_ICON_SX} />,
      hasEntityPassedFilter: member => !FaroCoreApiUtils.Member.isTeam(member)
    },
    team: {
      id: "team",
      label: capitalizeFirstLetter(TEAM_DISPLAY_NAME),
      icon: <TeamIcon sx={TYPE_ICON_SX} />,
      hasEntityPassedFilter: member => FaroCoreApiUtils.Member.isTeam(member)
    }
  };
}

/** The IDs of the options to filter by the seats, which are also their values in the URL */

/**
 * Returns the filter options for the seats the members hold, each only when the plan includes that
 * seat. "Sphere XG" matches either kind of Sphere XG seat. A team never holds a seat and is
 * therefore matched by none of them, not even by "No seat", since it could never get one either.
 *
 * @param seatsLookup The seats of the workspace, which also tell which seats the plan includes
 */
export function getMembersSeatFilterOptions(seatsLookup) {
  return {
    ...((seatsLookup.isActiveSeatPlan || seatsLookup.isUserRolePlan) && {
      xgSeat: {
        id: "xgSeat",
        label: MEMBER_SEAT_LABELS.xgSeat,
        hasEntityPassedFilter: member => hasAnyXgSeat(getHeldSeats(member, seatsLookup))
      }
    }),
    ...(seatsLookup.hasSceneSeats && {
      sceneSeat: {
        id: "sceneSeat",
        label: MEMBER_SEAT_LABELS.sceneSeat,
        hasEntityPassedFilter: member => getHeldSeats(member, seatsLookup).hasSceneSeat
      }
    }),
    noSeat: {
      id: "noSeat",
      label: "No seat",
      hasEntityPassedFilter: member => !FaroCoreApiUtils.Member.isTeam(member) && getSeatsRank(getHeldSeats(member, seatsLookup)) === 0
    }
  };
}